/**
 * particles.ts — falling letter-glyph particle physics and rendering (Phase 41 + 46).
 *
 * Phase 41: Particles spawn on correct letter typed, arc upward, fall under gravity,
 *   rotate and fade over 50 frames. All per-frame constants scaled by dt * 60 for
 *   frame-rate independence.
 *
 * Phase 46: Rendering changed from 8x8 fillRect squares to fillText glyphs.
 *   Each particle stores the fontSize of its source DOM element — headline letters
 *   fall large, body-text letters fall at reading size.
 *   PHYS-03 performance pattern: sort particles by fontSize before iterating so
 *   ctx.font is set only when fontSize changes, not per-particle.
 *   spawnParticle gains optional vx, vy, fontSize params for Phase 51 Bomb prep.
 */

import type { Particle } from './types';

const PARTICLE_COLOR = '#f5f0e8';           // warm cream — high contrast on dark canvas
const PARTICLE_LIFE = 50;
const PARTICLE_GRAVITY = 0.2;
const PARTICLE_FONT_FAMILY = '"Courier New", monospace'; // matches ENEMY_FONT family
const PARTICLE_FONT_DEFAULT = 14;            // fallback px when fontSize absent

export function spawnParticle(
  letter: string,
  x: number,
  y: number,
  particles: Particle[],
  vx?: number,
  vy?: number,
  fontSize?: number
): void {
  particles.push({
    letter,
    x,
    y,
    vx: vx !== undefined ? vx : Math.random() * 4 - 2,
    vy: vy !== undefined ? vy : -(3 + Math.random() * 3),
    angle: Math.random() * Math.PI * 2,
    angularVelocity: (Math.random() - 0.5) * 0.3,
    life: PARTICLE_LIFE,
    fontSize,
  });
}

export function updateParticles(particles: Particle[], dt: number): void {
  const scale = dt * 60;
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    p.vy += PARTICLE_GRAVITY * scale;
    p.x += p.vx * scale;
    p.y += p.vy * scale;
    p.angle += p.angularVelocity * scale;
    p.life -= scale;
    if (p.life <= 0) particles.splice(i, 1);
  }
}

export function drawParticles(
  ctx: CanvasRenderingContext2D,
  particles: Particle[]
): void {
  if (particles.length === 0) return;

  // PHYS-03: sort by fontSize ascending to minimize ctx.font mutations.
  // Sorting a copy avoids mutating the live state array order.
  const sorted = particles.slice().sort((a, b) =>
    (a.fontSize ?? PARTICLE_FONT_DEFAULT) - (b.fontSize ?? PARTICLE_FONT_DEFAULT)
  );

  ctx.save();
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillStyle = PARTICLE_COLOR;

  let currentFontSize = -1;

  for (const p of sorted) {
    const fs = Math.max(1, p.fontSize ?? PARTICLE_FONT_DEFAULT);

    // Set ctx.font only when fontSize changes — not per particle.
    if (fs !== currentFontSize) {
      ctx.font = `${fs}px ${PARTICLE_FONT_FAMILY}`;
      currentFontSize = fs;
    }

    const alpha = Math.max(0, p.life / PARTICLE_LIFE);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    ctx.fillText(p.letter, 0, 0);
    ctx.restore();
  }

  ctx.restore();
}
